import React from 'react';
import type { ServiceItem } from '../../types/services.types';
import { formatMoneyCurrency } from '@/shared/utils/formatMoneyDisplay';
import { formatUnitPriceNetFourPlaces } from '@/shared/utils/formatUnitPriceNet';
import type { PricedLineFigures } from '@features/quote/types/basketPricing.types';
import { pricedItemFigures } from '@features/quote/utils/pricedLines';

interface ServiceItemsListProps {
    items: ServiceItem[];
    showPricing?: boolean;
    className?: string;
    currency?: string;
    pricedLine?: PricedLineFigures;
}

export const ServiceItemsList: React.FC<ServiceItemsListProps> = ({
    items = [],
    showPricing = true,
    className = '',
    currency = 'GBP',
    pricedLine,
}) => {
    const lineFor = (item: ServiceItem) =>
        showPricing ? pricedItemFigures(pricedLine, item.pivot.item_id) : undefined;
    const hasSplit = items.some((item) => lineFor(item) !== undefined);

    if (!items || items.length === 0) {
        return (
            <div className={`text-mp-muted text-sm ${className}`}>
                No items specified for this service
            </div>
        );
    }

    return (
        <div className={`space-y-2 ${className}`}>
            <h4 className="font-medium text-sm text-mp-foreground mb-3">Service Items:</h4>

            <div className="space-y-2">
                {items.map((item) => {
                    const line = lineFor(item);
                    return (
                        <div
                            key={item.id}
                            className="flex justify-between items-center py-2 px-3 bg-mp-background rounded border border-mp-border"
                        >
                            <div className="flex-1">
                                <span className="text-sm text-mp-foreground">{item.description}</span>
                                <div className="text-xs text-mp-muted">
                                    Qty: {item.pivot.unit_quantity}
                                    {line && (
                                        <>
                                            {' × '}
                                            {formatUnitPriceNetFourPlaces(item, currency)}
                                        </>
                                    )}
                                </div>
                            </div>

                            {line && (
                                <div className="text-sm font-medium text-mp-foreground">
                                    {formatMoneyCurrency(line.net, currency)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {hasSplit && pricedLine && items.length > 1 && (
                <div className="border-t border-mp-border pt-2 mt-3">
                    <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-mp-foreground">
                            Items Total (Net):
                        </span>
                        <span className="text-sm font-semibold text-mp-foreground">
                            {formatMoneyCurrency(pricedLine.net, currency)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};
